import { Box } from '@mui/material';
import React, { useState } from 'react';
import { KitButton, KitCard, KitList } from '../common/MobileKit';
import { MobileListRow } from '../common/MobileCrud';
import { KitEmptyNote } from '../common/MobileDashboardKit';
import { KitField, KitSelect } from '../common/MobileKitExtras';

/**
 * Peças do Relatório de Inventário no shell mobile (cabeçalho do inventário e lista de produtos).
 * Só apresentação: dados, ordenação e formatação vêm de `pages/inventory/Report.js`.
 */

const diffColor = (diff) => (diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary');

// Cabeçalho do inventário (título, status, descrição/local, criado por) — mesmos textos do desktop.
export function InventoryHeader({ inventory, statusInfo, createdAt, finishedAt }) {
    return (
        <KitCard sx={{ mb: '14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                <Box sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.4, minWidth: 0 }}>{`Inventário #${inventory.code}`}</Box>
                <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', height: 24, p: '0 10px', borderRadius: '12px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', bgcolor: statusInfo.bg, color: statusInfo.color }}
                >
                    {statusInfo.label}
                </Box>
            </Box>
            <Box sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.4, mt: '4px' }}>
                {`${inventory.description || 'Sem descrição'}${inventory.location_label ? ` · Local: ${inventory.location_label}` : ''}`}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.4, mt: '4px' }}>
                {`Criado por ${inventory.created_by || '—'} em ${createdAt}${finishedAt ? ` · Finalizado em ${finishedAt}` : ''}`}
            </Box>
        </KitCard>
    );
}

// Ordenações do desktop (`sortableFields` do DefaultTable); padrão: Produto A-Z.
export const INVENTORY_SORTS = [
    { value: 'name:asc', label: 'Produto (A-Z)' },
    { value: 'name:desc', label: 'Produto (Z-A)' },
    { value: 'code:asc', label: 'Código (crescente)' },
    { value: 'code:desc', label: 'Código (decrescente)' },
    { value: 'expected_qty:asc', label: 'Sistema (menor)' },
    { value: 'expected_qty:desc', label: 'Sistema (maior)' },
    { value: 'counted_qty:asc', label: 'Contado (menor)' },
    { value: 'counted_qty:desc', label: 'Contado (maior)' },
    { value: 'difference:asc', label: 'Diferença (menor)' },
    { value: 'difference:desc', label: 'Diferença (maior)' },
];

const compare = (a, b) => {
    if (a === b) return 0;
    if (a === null || a === undefined) return 1;
    if (b === null || b === undefined) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'pt-BR', { numeric: true, sensitivity: 'base' });
};

/**
 * Lista de produtos do inventário (colunas do desktop: Código, Produto, Sistema, Contado, Diferença, Custo, Impacto).
 * - items: já filtrados por "somente divergentes"
 * - formatBRL(value): mesma formatação do desktop
 */
export function InventoryItemList({ items, formatBRL, pageSize = 20 }) {
    const [sort, setSort] = useState('name:asc');
    const [visible, setVisible] = useState(pageSize);
    const [field, dir] = sort.split(':');
    const sorted = [...items].sort((a, b) => (dir === 'desc' ? -1 : 1) * compare(a[field], b[field]));
    const shown = sorted.slice(0, visible);
    return (
        <>
            <KitField label="Ordenar por">
                <KitSelect value={sort} onChange={(e) => { setSort(e.target.value); setVisible(pageSize); }} aria-label="Ordenar por">
                    {INVENTORY_SORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </KitSelect>
            </KitField>
            {items.length === 0 ? (
                <KitCard><KitEmptyNote>Nenhum produto encontrado</KitEmptyNote></KitCard>
            ) : (
                <>
                    <KitList>
                        {shown.map((item, index) => {
                            const noCount = item.difference === null || item.difference === undefined;
                            return (
                                <MobileListRow
                                    key={`${item.code}-${index}`}
                                    title={item.name}
                                    sub={(
                                        <>
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{`Cód. ${item.code} · Custo ${formatBRL(item.cost)}`}</Box>
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{`Sistema ${item.expected_qty} · Contado ${item.counted_qty ?? '—'}`}</Box>
                                        </>
                                    )}
                                    end={(
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                            <Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4, color: noCount ? 'text.disabled' : diffColor(item.difference) }}>
                                                {noCount ? '—' : (item.difference > 0 ? `+${item.difference}` : item.difference)}
                                            </Box>
                                            <Box sx={{ fontWeight: 700, fontSize: 12, lineHeight: 1.4, color: noCount ? 'text.disabled' : diffColor(item.difference) }}>
                                                {noCount ? '—' : formatBRL(item.impact)}
                                            </Box>
                                        </Box>
                                    )}
                                />
                            );
                        })}
                    </KitList>
                    <Box sx={{ textAlign: 'center', fontSize: 12, color: 'text.disabled', m: '10px 0 6px', lineHeight: 1.4 }}>
                        Exibindo {shown.length} de {sorted.length} produtos
                    </Box>
                    {visible < sorted.length && (
                        <KitButton block sx={{ width: '100%' }} onClick={() => setVisible((v) => v + pageSize)}>Carregar mais</KitButton>
                    )}
                </>
            )}
        </>
    );
}

